import React, { useState, useEffect } from "react";
import api from "../../utils/api";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  IconButton,
  InputAdornment,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import { useCrypto } from "../../contexts/CryptoContext";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

const DepositModal = ({ open, onClose, onSuccess }) => {
  const { networks } = useCrypto();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [depositMethod, setDepositMethod] = useState("crypto");
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [amount, setAmount] = useState("");
  const [transactionHash, setTransactionHash] = useState("");
  const [step, setStep] = useState(1);
  const [adminBank, setAdminBank] = useState(null);
  const [loadingBank, setLoadingBank] = useState(false);
  const [bankError, setBankError] = useState(null);
  const [bankTransferDetails, setBankTransferDetails] = useState({
    amount: "",
    transactionReference: "",
    senderName: "",
  });
  const [submittingBank, setSubmittingBank] = useState(false);

  // Reset form fields when modal closes
  useEffect(() => {
    if (!open) {
      setAmount("");
      setTransactionHash("");
      setBankTransferDetails({
        amount: "",
        transactionReference: "",
        senderName: "",
      });
    }
  }, [open]);

  // Fetch admin bank details when bank transfer is selected
  useEffect(() => {
    if (depositMethod === "bank_transfer" && open) {
      fetchAdminBank();
    }
  }, [depositMethod, open]);

  const fetchAdminBank = async () => {
    try {
      setLoadingBank(true);
      setBankError(null);
      const response = await api.get("/banks/admin", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
      });

      if (response.data.status === "success") {
        setAdminBank(response.data.data.bank);
      }
    } catch (error) {
      setBankError(
        error.response?.data?.message || "Failed to fetch bank details"
      );
      enqueueSnackbar("Failed to load admin bank details", {
        variant: "error",
      });
    } finally {
      setLoadingBank(false);
    }
  };

  const handleMethodChange = (event, newValue) => {
    setDepositMethod(newValue);
    if (newValue === "bank_transfer" && !adminBank) {
      fetchAdminBank();
    }
  };

  const handleNetworkSelect = (event) => {
    setSelectedNetwork(event.target.value);
    setStep(2);
  };

  const handleBankTransferChange = (e) => {
    setBankTransferDetails({
      ...bankTransferDetails,
      [e.target.name]: e.target.value,
    });
  };

  const network = networks.find((n) => n._id === selectedNetwork);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar("Copied to clipboard!", { variant: "success" });
  };

  const handleSubmit = async () => {
    try {
      const response = await api.post(
        "/crypto/deposit",
        {
          network: network.name,
          cryptoAmount: Number(amount),
          transactionHash,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      );

      if (response.data.status === "success") {
        onSuccess();
        onClose();
        enqueueSnackbar("Deposit submitted successfully!", {
          variant: "success",
        });
      }
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Failed to submit deposit",
        { variant: "error" }
      );
    }
  };

  const handleBankSubmit = async () => {
    try {
      setSubmittingBank(true);
      const today = new Date().toISOString().split("T")[0]; // Format: YYYY-MM-DD

      const response = await api.post(
        "/users/notify-admin",
        {
          accountName: adminBank.accountName,
          amount: bankTransferDetails.amount,
          bank: adminBank.bankName,
          date: today,
          senderName: bankTransferDetails.senderName,
          transactionReference: bankTransferDetails.transactionReference,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      );

      if (response.data.status === "success") {
        onSuccess();
        onClose();
        enqueueSnackbar("Bank transfer notification submitted successfully!", {
          variant: "success",
        });
      }
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message ||
          "Failed to submit bank transfer notification",
        { variant: "error" }
      );
    } finally {
      setSubmittingBank(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Deposit Funds</DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={depositMethod}
            onChange={handleMethodChange}
            variant="fullWidth"
          >
            <Tab label="Crypto" value="crypto" />
            <Tab label="Bank Transfer" value="bank_transfer" />
          </Tabs>
        </Box>

        {depositMethod === "crypto" && (
          <>
            {step === 1 && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Select Network</InputLabel>
                <Select
                  value={selectedNetwork}
                  onChange={handleNetworkSelect}
                  label="Select Network"
                >
                  {networks.map((network) => (
                    <MenuItem key={network._id} value={network._id}>
                      {network.name} ({network.symbol})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {step === 2 && network && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Send {network.name} to this address on the network{" "}
                  {network.symbol}
                </Typography>
                <TextField
                  fullWidth
                  value={network.walletAddress}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleCopy(network.walletAddress)}
                        >
                          <ContentCopy />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                <Alert severity="info" sx={{ mb: 2 }}>
                  Exchange Rate: 1 {network.name} = ₦
                  {network.exchangeRate.toLocaleString()}
                  <br />
                  Minimum Deposit: ${network.minimumDeposit} {network.name}
                </Alert>

                <TextField
                  fullWidth
                  label={`Amount (${network.name})`}
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Transaction Hash"
                  value={transactionHash}
                  onChange={(e) => setTransactionHash(e.target.value)}
                  helperText="Enter the transaction hash after sending the payment"
                />

                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mt: 2 }}
                >
                  You will receive: ₦
                  {(amount * network.exchangeRate).toLocaleString()}
                </Typography>
              </Box>
            )}
          </>
        )}

        {depositMethod === "bank_transfer" && (
          <Box sx={{ mt: 2 }}>
            {loadingBank ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                <CircularProgress />
              </Box>
            ) : bankError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {bankError}
              </Alert>
            ) : adminBank ? (
              <>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Please transfer funds to the bank account below. After making
                  the payment, fill in the form to notify us.
                </Alert>

                <Typography variant="h6" gutterBottom>
                  Bank Transfer Details
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Bank Name
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body1">
                      {adminBank.bankName}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(adminBank.bankName)}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Account Number
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body1">
                      {adminBank.accountNumber}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(adminBank.accountNumber)}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Account Name
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body1">
                      {adminBank.accountName}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(adminBank.accountName)}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                  Notify us of your transfer
                </Typography>

                <TextField
                  fullWidth
                  label="Amount (₦)"
                  name="amount"
                  type="number"
                  value={bankTransferDetails.amount}
                  onChange={handleBankTransferChange}
                  margin="normal"
                  required
                />

                <TextField
                  fullWidth
                  label="Transaction Reference"
                  name="transactionReference"
                  value={bankTransferDetails.transactionReference}
                  onChange={handleBankTransferChange}
                  margin="normal"
                  required
                  helperText="Enter the reference number from your bank transfer"
                />

                <TextField
                  fullWidth
                  label="Sender Name"
                  name="senderName"
                  value={bankTransferDetails.senderName}
                  onChange={handleBankTransferChange}
                  margin="normal"
                  required
                  helperText="Name as it appears on your bank account"
                />
              </>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Admin bank details are currently unavailable. Please try again
                later or contact support.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {depositMethod === "crypto" && step === 2 && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !amount || !transactionHash || amount < network?.minimumDeposit
            }
          >
            Submit
          </Button>
        )}
        {depositMethod === "bank_transfer" && adminBank && (
          <Button
            variant="contained"
            onClick={handleBankSubmit}
            disabled={
              submittingBank ||
              !bankTransferDetails.amount ||
              !bankTransferDetails.transactionReference ||
              !bankTransferDetails.senderName
            }
          >
            {submittingBank ? <CircularProgress size={24} /> : "submit"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DepositModal;
